import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { watchlistAPI } from '../services/api';
import { HiOutlineVideoCamera, HiOutlineTrash, HiOutlineChevronRight, HiOutlineX, HiOutlineRefresh, HiOutlineSearch, HiOutlineShare } from 'react-icons/hi';
import toast from 'react-hot-toast';
import ConfirmModal from '../components/ConfirmModal';
import { StarRating } from '../components';

const TMDB_IMAGE_BASE = 'https://image.tmdb.org/t/p/w200';

function Series() {
  const { t } = useTranslation();
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('date');
  const [dateSortDirection, setDateSortDirection] = useState('desc');
  const [alphabeticalSortDirection, setAlphabeticalSortDirection] = useState('asc');
  const [progressSortDirection, setProgressSortDirection] = useState('desc');
  const [searchQuery, setSearchQuery] = useState('');
  const [hideCompleted, setHideCompleted] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState(0);
  const [syncingCurrent, setSyncingCurrent] = useState(0);
  const [syncingTotal, setSyncingTotal] = useState(0);
  const [syncingTitle, setSyncingTitle] = useState('');
  const [eventSource, setEventSource] = useState(null);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    seriesId: null,
    event: null,
  });
  const navigate = useNavigate();

  useEffect(() => {
    loadSeries();
  }, []);

  const loadSeries = async () => {
    setLoading(true);
    try {
      const response = await watchlistAPI.getTV();
      setSeries(response.data);
    } catch (error) {
      console.error('Error loading series:', error);
      toast.error(t('series.errorLoadingSeries'));
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveClick = (id, e) => {
    e.stopPropagation();
    setConfirmModal({ isOpen: true, seriesId: id, event: e });
  };

  const removeSeries = async () => {
    if (!confirmModal.seriesId) return;
    
    try {
      await watchlistAPI.remove(confirmModal.seriesId);
      setSeries((prev) => prev.filter((s) => s.id !== confirmModal.seriesId));
      toast.success(t('series.seriesRemoved'));
      setConfirmModal({ isOpen: false, seriesId: null, event: null });
    } catch (error) {
      toast.error(t('series.errorRemoving'));
    }
  };

  const handleRate = async (showId, rating) => {
    try {
      await watchlistAPI.update(showId, { user_rating: rating });
      setSeries(prev => prev.map(s =>
        s.id === showId ? { ...s, user_rating: rating } : s
      ));
      toast.success(rating > 0 ? t('rating.saved') : t('rating.removed'));
    } catch (error) {
      toast.error(t('rating.error'));
    }
  };

  const getProgress = (show) => {
    if (!show.seasons || show.seasons.length === 0) return 0;

    let totalEpisodes = 0;
    let watchedEpisodes = 0;

    show.seasons.forEach((season) => {
      if (season.watched) {
        totalEpisodes += season.episodes?.length || 0;
        watchedEpisodes += season.episodes?.length || 0;
      } else if (season.episodes) {
        season.episodes.forEach((ep) => {
          totalEpisodes++;
          if (ep.watched) watchedEpisodes++;
        });
      }
    });

    return totalEpisodes === 0 ? 0 : Math.round((watchedEpisodes / totalEpisodes) * 100);
  };

  const fullyWatchedCount = series.filter(show => getProgress(show) === 100).length;

  const handleDateSort = () => {
    if (sortBy === 'date') {
      setDateSortDirection(dateSortDirection === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy('date');
      setDateSortDirection('desc');
    }
  };

  const handleAlphabeticalSort = () => {
    if (sortBy === 'alphabetical') {
      setAlphabeticalSortDirection(alphabeticalSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy('alphabetical');
      setAlphabeticalSortDirection('asc');
    }
  };

  const handleProgressSort = () => {
    if (sortBy === 'progress') {
      setProgressSortDirection(progressSortDirection === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy('progress');
      setProgressSortDirection('desc');
    }
  };

  const sortedSeries = [...series]
    .filter(s => {
      // Search filter
      if (searchQuery.trim() !== '' && !s.title.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
      // Hide completed filter
      if (hideCompleted && getProgress(s) === 100) {
        return false;
      }
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'date') {
        const dateA = new Date(a.added_at || 0);
        const dateB = new Date(b.added_at || 0);
        return dateSortDirection === 'desc' ? dateB - dateA : dateA - dateB;
      } else if (sortBy === 'alphabetical') {
        const result = a.title.localeCompare(b.title, 'pt-BR');
        return alphabeticalSortDirection === 'asc' ? result : -result;
      } else if (sortBy === 'progress') {
        const progressA = getProgress(a);
        const progressB = getProgress(b);
        return progressSortDirection === 'desc' ? progressB - progressA : progressA - progressB;
      }
    return 0;
  });

  const clearSearch = () => {
    setSearchQuery('');
  };

  const handleShareList = async () => {
    const rated = series.filter(s => s.user_rating > 0).sort((a, b) => b.user_rating - a.user_rating);
    const completed = series.filter(s => getProgress(s) === 100 && !s.user_rating);
    const inProgress = series.filter(s => {
      const p = getProgress(s);
      return p > 0 && p < 100 && !s.user_rating;
    });
    const notStarted = series.filter(s => getProgress(s) === 0 && !s.user_rating);

    let text = `📺 ${t('series.mySeries')} — My Series\n`;
    text += `📊 ${series.length} ${t('share.seriesInList')} • ${fullyWatchedCount} ${t('share.completedLabel')}\n`;

    if (rated.length > 0) {
      text += `\n⭐ ${t('share.myRatings')}:\n`;
      rated.forEach(s => {
        const progress = getProgress(s);
        text += `★ ${s.user_rating} — ${s.title} (${progress}%)\n`;
      });
    }
    if (completed.length > 0) {
      text += `\n✅ ${t('share.completed')}:\n`;
      completed.forEach(s => { text += `• ${s.title}\n`; });
    }
    if (inProgress.length > 0) {
      text += `\n📺 ${t('share.inProgress')}:\n`;
      inProgress.forEach(s => {
        text += `• ${s.title} — ${getProgress(s)}%\n`;
      });
    }
    if (notStarted.length > 0) {
      text += `\n🎯 ${t('share.wantToWatch')}:\n`;
      notStarted.forEach(s => { text += `• ${s.title}\n`; });
    }

    const copyToClipboard = (value) => {
      const textarea = document.createElement('textarea');
      textarea.value = value;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      toast.success(t('share.copied'));
    };

    try {
      const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
      if (navigator.share && isMobile) {
        await navigator.share({ title: t('series.mySeries'), text });
      } else if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        toast.success(t('share.copied'));
      } else {
        copyToClipboard(text);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        try {
          copyToClipboard(text);
        } catch {
          toast.error(t('share.error'));
        }
      }
    }
  };

  const handleSyncUserSeries = async () => {
    setSyncing(true);
    setSyncProgress(0);
    setSyncingCurrent(0);
    setSyncingTotal(0);
    setSyncingTitle(t('common.initializing'));

    const connection = watchlistAPI.syncUserTVShows({
      onProgress: (data) => {
        setSyncProgress(data.progress);
        setSyncingCurrent(data.current);
        setSyncingTotal(data.total);
        setSyncingTitle(data.title || t('common.initializing'));
      },
      onComplete: (data) => {
        setSyncing(false);
        setSyncProgress(100);
        if (data.success > 0) toast.success(t('settings.syncComplete'));
        if (data.failed > 0) toast.error(t('settings.syncError'));
        loadSeries(); // Reload series list
      },
      onError: (data) => {
        setSyncing(false);
        toast.error(t('settings.syncError'));
      },
    });
    setEventSource(connection);
  };

  const cancelSync = () => {
    if (eventSource) eventSource.close();
    setSyncing(false);
    toast(t('common.cancel'));
  };

  return (
    <div>
      {/* Sync Modal */}
      {syncing && (
        <div className="desktop-sync-modal">
          <div className="desktop-sync-modal__overlay" onClick={cancelSync} />
          <div className="desktop-sync-modal__content">
            <div className="desktop-sync-modal__icon">
              <HiOutlineRefresh size={40} className="spinning" />
            </div>
            <h3 className="desktop-sync-modal__title">{t('settings.syncing')}</h3>
            <p className="desktop-sync-modal__series">{syncingTitle}</p>
            
            <div className="desktop-sync-modal__progress-bar">
              <div 
                className="desktop-sync-modal__progress-fill"
                style={{ width: `${syncProgress}%` }}
              />
            </div>
            
            <div className="desktop-sync-modal__stats">
              <span>{syncingCurrent}/{syncingTotal}</span>
              <span>{syncProgress}%</span>
            </div>
            
            <button 
              className="desktop-sync-modal__cancel"
              onClick={cancelSync}
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}

      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1>{t('series.mySeries')}</h1>
          <p>{series.length} {t('series.seriesInList')} • {fullyWatchedCount} {fullyWatchedCount === 1 ? t('series.seriesFullyWatchedSingular') : t('series.seriesFullyWatchedPlural')}</p>
          <button
            className="sort-btn"
            onClick={handleShareList}
            title={t('share.shareList')}
            style={{ marginTop: '8px' }}
          >
            <HiOutlineShare size={16} />
            {t('share.shareList')}
          </button>
          {/* Mobile sort indicator */}
          <div className="mobile-sort-indicator">
            {sortBy === 'date' && (
              <span>
                📅 {dateSortDirection === 'desc' ? t('series.newest') : t('series.oldest')}
              </span>
            )}
            {sortBy === 'alphabetical' && (
              <span>
                🔤 {alphabeticalSortDirection === 'asc' ? 'A-Z' : 'Z-A'}
              </span>
            )}
            {sortBy === 'progress' && (
              <span>
                📊 {progressSortDirection === 'desc' ? t('series.highestProgress') : t('series.lowestProgress')}
              </span>
            )}
          </div>
        </div>

        {/* Right Side: Update Button */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0, marginLeft: 'auto', marginRight: '50px' }}>
          <button
            type="button"
            onClick={handleSyncUserSeries}
            className="btn btn-primary"
            disabled={syncing}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              opacity: syncing ? 0.6 : 1,
              cursor: syncing ? 'not-allowed' : 'pointer',
              whiteSpace: 'nowrap'
            }}
          >
            <HiOutlineRefresh size={16} className={syncing ? 'spinning' : ''} />
            {t('series.updateSeries')}
          </button>
        </div>
      </div>

      <div className="sort-controls sort-controls--series" style={{ alignItems: 'center' }}>
        <div className="sort-buttons">
          <span className="sort-label">{t('series.sortBy')}:</span>
          <button
            className={`sort-btn ${sortBy === 'date' ? 'active' : ''}`}
            onClick={handleDateSort}
          >
            <span className="sort-icon">📅</span>
            {t('series.dateAdded')}
          </button>
          <button
            className={`sort-btn ${sortBy === 'alphabetical' ? 'active' : ''}`}
            onClick={handleAlphabeticalSort}
          >
            <span className="sort-icon">🔤</span>
            {t('series.alphabeticalOrder')}
          </button>
          <button
            className={`sort-btn ${sortBy === 'progress' ? 'active' : ''}`}
            onClick={handleProgressSort}
          >
            <span className="sort-icon">📊</span>
            {t('series.progress')}
          </button>
          {/* Toggle Hide Completed */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '8px' }}>
            <label className="toggle-switch toggle-switch--small">
              <input
                type="checkbox"
                checked={hideCompleted}
                onChange={(e) => setHideCompleted(e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
            <span style={{ fontSize: '13px', fontWeight: '500', color: 'var(--text-secondary)' }}>
              {t('series.hideCompleted')}
            </span>
          </div>
          <div className="search-container" style={{ marginBottom: 0, maxWidth: '300px', height: '38px' }}>
            <HiOutlineSearch className="search-icon" style={{ width: '18px', height: '18px' }} />
            <input
              type="text"
              className="search-input"
              placeholder={t('series.searchSeries')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ height: '38px', minHeight: '38px', padding: '0 40px 0 40px' }}
            />
            {searchQuery && (
              <button
                className="search-clear-btn"
                onClick={clearSearch}
                aria-label="Clear search"
                style={{ width: '20px', height: '20px', right: '10px' }}
              >
                <HiOutlineX size={14} />
              </button>
            )}
          </div>
        </div>
        {sortBy === 'date' && (
          <div className="sort-indicator">
            <span className="sort-direction">
              {dateSortDirection === 'desc' ? '⬇️' : '⬆️'}
            </span>
            <span className="sort-text">
              {dateSortDirection === 'desc' ? t('series.newest') : t('series.oldest')}
            </span>
          </div>
        )}
        {sortBy === 'alphabetical' && (
          <div className="sort-indicator">
            <span className="sort-direction">
              {alphabeticalSortDirection === 'asc' ? '🔤' : '🔠'}
            </span>
            <span className="sort-text">
              {alphabeticalSortDirection === 'asc' ? 'A-Z' : 'Z-A'}
            </span>
          </div>
        )}
        {sortBy === 'progress' && (
          <div className="sort-indicator">
            <span className="sort-direction">
              {progressSortDirection === 'desc' ? '⬇️' : '⬆️'}
            </span>
            <span className="sort-text">
              {progressSortDirection === 'desc' ? t('series.highestProgress') : t('series.lowestProgress')}
            </span>
          </div>
        )}
      </div>

      <div className="series-list-scrollable">
        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        ) : sortedSeries.length > 0 ? (
          <div>
            {sortedSeries.map((show) => {
              const progress = getProgress(show);
              return (
                <div
                  key={show.id}
                  className="watchlist-item"
                  onClick={() => navigate(`/series/${show.id}`)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="watchlist-item-poster">
                    {show.poster_path ? (
                      <img
                        src={`${TMDB_IMAGE_BASE}${show.poster_path}`}
                        alt={show.title}
                      />
                    ) : (
                      <div
                        style={{
                          width: '100%',
                          height: '100%',
                          background: '#e5e7eb',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <HiOutlineVideoCamera size={24} color="#9ca3af" />
                      </div>
                    )}
                  </div>
                  <div className="watchlist-item-content">
                    <h3 className="watchlist-item-title">{show.title}</h3>
                    <p className="watchlist-item-meta">
                      {show.release_date
                        ? new Date(show.release_date).getFullYear()
                        : t('series.unknownYear')}
                      {show.release_date && (
                        show.status === 'Ended' || show.status === 'Canceled'
                          ? ` - ${show.last_air_date ? new Date(show.last_air_date).getFullYear() : ''}`
                          : ' - '
                      )}
                      {show.number_of_episodes > 0 && ` • ${show.number_of_episodes} eps`}
                      {show.vote_average > 0 && ` • ⭐ ${show.vote_average.toFixed(1)}`}
                    </p>
                    <div className="star-rating-with-value">
                      <StarRating
                        rating={show.user_rating || 0}
                        onRate={(rating) => handleRate(show.id, rating)}
                        size={18}
                      />
                      {show.user_rating > 0 && (
                        <span className="star-rating-value">{show.user_rating} / 5</span>
                      )}
                    </div>

                    {/* Progress bar */}
                    <div style={{ marginTop: '12px' }}>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          marginBottom: '6px',
                          fontSize: '12px',
                          color: '#6b7280',
                        }}
                      >
                        <span>{t('series.progress')}</span>
                        <span>{progress}%</span>
                      </div>
                      <div
                        style={{
                          height: '6px',
                          background: '#e5e7eb',
                          borderRadius: '3px',
                          overflow: 'hidden',
                        }}
                      >
                        <div
                          style={{
                            height: '100%',
                            width: `${progress}%`,
                            background:
                              progress === 100
                                ? 'var(--success)'
                                : 'linear-gradient(90deg, var(--primary), var(--secondary))',
                            borderRadius: '3px',
                            transition: 'width 0.3s ease',
                          }}
                        />
                      </div>
                    </div>

                    <div className="watchlist-item-actions" style={{ marginTop: '12px' }}>
                      <button
                        className="btn btn-secondary"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/series/${show.id}`);
                        }}
                      >
                        <HiOutlineChevronRight />
                        {t('series.viewEpisodes')}
                      </button>
                      <button
                        className="btn btn-icon"
                        onClick={(e) => handleRemoveClick(show.id, e)}
                        title={t('series.remove')}
                      >
                        <HiOutlineTrash />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-state-icon">
              <HiOutlineVideoCamera size={32} />
            </div>
            <h3>{t('series.noSeriesFound')}</h3>
            <p>{t('series.addSeriesFromSearch')}</p>
          </div>
        )}
      </div>

      {/* Confirm Remove Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={t('series.removeSeries')}
        message={t('series.confirmRemove')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        onConfirm={removeSeries}
        onCancel={() => setConfirmModal({ isOpen: false, seriesId: null, event: null })}
        type="danger"
      />
    </div>
  );
}

export default Series;
